import { configurePropertyMap } from 'modules/journey/components/FilterInputsList/FilterInputsList.utils';
import { ConditionSingleValueStringSelect as ConditionValueSingleStringSelect } from 'ui/queryBuilder';
import { getDefaultPropertyMapConfig } from 'modules/journey/components/FilterInputsList/FilterInputsList.utils';
import {
  getStartNodeTriggerDataType,
  getStartNodeTriggerNodeClass,
  getStartNodeTriggerEventName,
} from 'modules/workflows/ducks/selectors';
import { useSelector } from 'react-redux';
import { ConditionSingleValueSelectProps } from 'ui/queryBuilder/types';
import { SelectOption } from 'modules/queryBuilder/contexts/visualComponents';
import { WORKFLOW_TRIGGERS } from 'modules/workflows/constants';
import { useShouldFilterFieldsAndProperties } from './useShouldFilterFieldsAndProperties';
import { useMemo } from 'react';

export interface ESFieldTypeDefinition {
  /** The type of field.  EG: 'date', 'string', 'long' etc */
  fieldType: string;
  /** The name of the field */
  field: string;
  // add this as a pass through for the new query builder
  title?: string;
}

export interface ESFieldTypeMap {
  [fieldName: string]: ESFieldTypeDefinition;
}

export const JourneyFieldSelect = ({
  options,
  ...props
}: ConditionSingleValueSelectProps<string>) => {
  const startNodeNodeClass = useSelector(getStartNodeTriggerNodeClass);
  const startNodeDataType = useSelector(getStartNodeTriggerDataType);
  const startNodeEventName = useSelector(getStartNodeTriggerEventName);
  const shouldFilterFieldsAndProperties = useShouldFilterFieldsAndProperties();
  const isAddedItemToCart = startNodeNodeClass === WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART;

  const config = getDefaultPropertyMapConfig({
    dataType: (isAddedItemToCart ? startNodeNodeClass : '') || startNodeDataType,
    filterString: startNodeEventName,
  });

  // translate the options from the new query builder to the old query builder
  const optionsMap = useMemo(
    () =>
      (options ?? []).reduce(
        (
          acc: Record<string, { fieldType: string; field: string; title: string }>,
          option: { title: string; badge: string; value: string } | SelectOption<string>,
        ) => {
          acc[option.value] = {
            fieldType: option.badge ?? '',
            field: option.value,
            title: option.title,
          };
          return acc;
        },
        {},
      ),
    [options],
  );

  const propertyMap = useMemo(
    () => configurePropertyMap(optionsMap, config),
    [optionsMap, config],
  ) as ESFieldTypeMap;

  // translate the options from the old query builder to the new query builder
  const newOptions = useMemo(
    () =>
      Object.values(propertyMap).map((option: ESFieldTypeDefinition) => ({
        title: option.title,
        badge: option.fieldType as string,
        value: option.field,
      })),
    [propertyMap],
  ) as SelectOption<string>[];

  return (
    <ConditionValueSingleStringSelect
      options={shouldFilterFieldsAndProperties ? newOptions : options}
      {...props}
    />
  );
};
